import { MutationKey, useMutation, UseMutationResult } from "react-query";
import Axios from "axios";
import { HttpMethod } from "./types";
import { useSnackbar } from "notistack";

const BODYLESS_METHODS: HttpMethod[] = ["get", "delete", "head", "options"];

/**
 * Adds mutateAsyncSafe to a mutation result. The returned promise never rejects;
 * errors are still handled by the mutation. Use when calling from UI so callers don't need try/catch.
 */
export function withMutateAsyncSafe<TData, TError, TVariables>(
  mutation: UseMutationResult<TData, TError, TVariables>
) {
  return {
    ...mutation,
    mutateAsyncSafe: async (variables: TVariables) => {
      try {
        await mutation.mutateAsync(variables);
      } catch {
        // Error already handled by mutation
      }
    }
  };
}

export type UseRemoteMutationOptions<ResponseType, BodyType> = {
  key: MutationKey;
  /** URL or function that receives variables (e.g. for path params). */
  url: string | ((data: BodyType) => string);
  method?: HttpMethod;
  params?: { [key: string]: unknown };
  headers?: { [key: string]: string };
  /** When set, use this as request body instead of the raw variables (e.g. omit path params). */
  body?: (data: BodyType) => unknown;
  snackbarTextOnFail?: string;
  snackbarTextOnSuccess?: string;
  snackbarTextFunOnFail?: (error: unknown) => string;
  onSuccess?: (data: ResponseType, variables: BodyType) => void;
};

/**
 * Creates a mutation using react-query. Default method is POST.
 * Returns the mutation with mutateAsyncSafe so UI callers get a promise that never rejects.
 */
export function useRemoteMutation<ResponseType, ErrorType, BodyType = never>(
  options: UseRemoteMutationOptions<ResponseType, BodyType>
) {
  const {
    key,
    url,
    method = "post",
    params,
    headers,
    body,
    snackbarTextOnSuccess,
    snackbarTextOnFail,
    snackbarTextFunOnFail,
    onSuccess
  } = options;
  const snackbar = useSnackbar();

  const mutation = useMutation<ResponseType, ErrorType, BodyType>(
    key,
    async (data: BodyType): Promise<ResponseType> => {
      const resolvedUrl = typeof url === "function" ? url(data) : url;
      const requestBody = body ? body(data) : data;
      const config = { params, headers };

      try {
        const response = BODYLESS_METHODS.includes(method)
          ? await Axios[method]<ResponseType>(resolvedUrl, config)
          : await Axios[method]<ResponseType>(resolvedUrl, requestBody, config);
        if (snackbarTextOnSuccess)
          snackbar.enqueueSnackbar(snackbarTextOnSuccess, { variant: "success" });
        return response.data;
      } catch (error) {
        const message = (await snackbarTextFunOnFail?.(error)) ?? snackbarTextOnFail;
        if (message !== undefined) snackbar.enqueueSnackbar(message, { variant: "error" });
        throw error;
      }
    },
    { onSuccess }
  );

  return withMutateAsyncSafe(mutation);
}
